/**
 * The lab builder: a tree on the left, whatever is selected on the right.
 *
 * A lab is three tiers deep - lab, section, configuration item - and every tier is shared with other
 * labs. A flat form cannot show that. It cannot show that section two is also in four other labs, it
 * cannot show that the item you are about to edit is attached in nine places, and it cannot show that
 * the order the rows are listed in is not the order they run in. The tree can, so the tree is the
 * primary control and the panel beside it is the detail.
 *
 * Two buttons at every level, deliberately. `+ Add New` authors something and attaches it here;
 * `+ Attach Existing` reaches into the library. The second is the one that keeps the catalogue from
 * turning into twelve near-identical copies of the same section, so it is offered every bit as
 * prominently as the first rather than hidden behind "advanced".
 *
 * Everything expensive is fetched once and filtered in memory: one attachment list and one device
 * list for the whole tree, rather than a hook per expanded section. Expanding a section would
 * otherwise fire a request, and expanding six would fire six - and the two tables are small.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HbrIcon } from '@harbor/elements-react-standalone';
import {
  arrowSquareOut,
  arrowsClockwise,
  copy,
  pencilSimple,
  plus,
  x,
} from '@harbor/elements/icons';
import StbButton from '../security-toolbox-ui/StbButton';
import {
  useAttachLabDevices,
  useAttachLabScenarioItems,
  useAttachLabScenarios,
  useCompileLabScenario,
  useDetachLabDevice,
  useDetachLabScenario,
  useDetachLabScenarioItem,
  useLab,
  useLabAttachments,
  useLabConfigurationItems,
  useLabDeviceTypes,
  useLabDevices,
  useLabExecutionMethods,
  useLabProducts,
  useLabScenario,
  useLabScenarios,
  useReorderLabScenarios,
  useSaveLabRow,
} from '../../hooks/lab-hooks';
import {
  Lab,
  LabConfiguration,
  LabConfigurationItem,
  LabDevice,
  LabScenario,
  LabScenarioDeviceLink,
  LabScenarioItem,
  LabScenarioLink,
  LAB_TABLES,
  labConfigurationBlockers,
  labDeviceBlockers,
  labItemBlockers,
  labItemOperationLabel,
  labItemSummary,
  labSizeLabel,
  labSlugify,
} from '../../support/labService';
import {
  LabBlockerList,
  LabDate,
  LabMessage,
  LabPanelState,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';
import {
  LabAuthoringStatus,
  LabChip,
  LabDependencyMark,
  LabDragHandle,
  LabRowAction,
  LabRowMenu,
  LabSaveDestination,
  LabSection,
  LabSlideOver,
  LabStatusDot,
  LabStatusLegend,
  LabTwisty,
  LabUsageBadge,
  useLabFlash,
  useLabToasts,
} from './LabAdminShared';
import LabAttachPicker from './LabAttachPicker';
import LabConfigurationItemForm, {
  labItemFormBlockers,
  LAB_ITEM_EMPTY,
} from './LabConfigurationItemForm';
import LabDeviceForm, { labDeviceEmpty, labDeviceFormBlockers } from './LabDeviceForm';
import LabScenarioForm, {
  labScenarioFormBlockers,
  labScenarioSlug,
  LAB_SCENARIO_EMPTY,
} from './LabScenarioForm';
import LabConfigurationsPanel from './LabConfigurationsPanel';
import LabDependencyView from './LabDependencyView';
import LabDeviceAddressPanel from './LabDeviceAddressPanel';
import LabScenarioItemsPanel from './LabScenarioItemsPanel';
import {
  LabScenarioCriteriaPanel,
  LabScenarioStepsPanel,
  LabScenarioTriggersPanel,
} from './LabScenarioPanels';

/**
 * What the tree has selected.
 *
 * Carries its own label so the shell can draw a breadcrumb without loading the tree's data twice.
 * The deeper kinds carry their ancestors too, because "this item, in this section" is the selection -
 * the same item is in other sections, and detaching from the wrong one is not recoverable by undo.
 */
export type LabBuilderNode =
  | { kind: 'lab'; label: string }
  | { kind: 'scenario'; scenarioId: number; linkId: number; label: string }
  | {
      kind: 'item';
      scenarioId: number;
      attachmentId: number;
      itemId: number;
      label: string;
    }
  | { kind: 'device'; scenarioId: number; deviceId: number; label: string }
  | { kind: 'publication'; configurationId: number; label: string };

export type LabBuilderProps = {
  labId: number;
  canEdit?: boolean;
  /** Owned by the shell, which draws the breadcrumb from it and scopes its "+ New" menu by it. */
  selected: LabBuilderNode;
  onSelect: (node: LabBuilderNode) => void;
  /** Opens a row in its library view, for "View in library" on the row menus. */
  onOpenInLibrary?: (kind: 'scenario' | 'item' | 'device', id: number) => void;
};

/** What is being created in the slide-over, and where it will be put. */
type Creating =
  | { kind: 'scenario' }
  | { kind: 'item'; scenarioId: number }
  | { kind: 'device'; scenarioId: number }
  | null;

/** What the attach picker is picking, and for whom. */
type Attaching =
  | { kind: 'scenario' }
  | { kind: 'item'; scenarioId: number }
  | { kind: 'device'; scenarioId: number }
  | null;

const treeRow = (selected: boolean, flashed: boolean, depth: number) =>
  [
    'flex items-center gap-1.5 rounded px-1.5 py-1 text-sm',
    selected ? 'bg-blue-100' : flashed ? 'bg-amber-100' : 'hover:bg-gray-100',
    depth === 0 ? 'font-semibold' : '',
  ].join(' ');

const LabBuilder: React.FC<LabBuilderProps> = ({
  labId,
  canEdit = false,
  selected,
  onSelect,
  onOpenInLibrary,
}) => {
  const [expanded, setExpanded] = useState<number[]>([]);
  const [creating, setCreating] = useState<Creating>(null);
  const [attaching, setAttaching] = useState<Attaching>(null);
  const [dragLinkId, setDragLinkId] = useState<number | null>(null);

  const toasts = useLabToasts();
  const { flashed, flash } = useLabFlash();

  const lab = useLab(labId);
  const scenarios = useLabScenarios();
  const attachments = useLabAttachments();
  const devices = useLabDevices();
  const catalogue = useLabConfigurationItems();
  const deviceTypes = useLabDeviceTypes();
  const methods = useLabExecutionMethods();

  const attachScenarios = useAttachLabScenarios();
  const detachScenario = useDetachLabScenario();
  const reorder = useReorderLabScenarios();
  const attachItems = useAttachLabScenarioItems();
  const detachItem = useDetachLabScenarioItem();
  const attachDevices = useAttachLabDevices();
  const detachDevice = useDetachLabDevice();
  const compile = useCompileLabScenario();
  const saveLink = useSaveLabRow<Partial<LabScenarioLink> & { id?: number }>(
    LAB_TABLES.scenarioLink,
  );

  const row = lab.data || null;

  /** Sections in the order the runtime will read them, with the disabled ones still shown. */
  const links = useMemo(
    () =>
      [...(row?.scenario_links || [])].sort(
        (left, right) => (left.order || 0) - (right.order || 0) || left.id - right.id,
      ),
    [row?.scenario_links],
  );

  const publications = useMemo(() => row?.configurations || [], [row?.configurations]);

  const scenarioById = useMemo(
    () => new Map((scenarios.data || []).map((scenario) => [scenario.id, scenario])),
    [scenarios.data],
  );

  const itemById = useMemo(
    () => new Map((catalogue.data || []).map((item) => [item.id, item])),
    [catalogue.data],
  );

  /** One pass over each list, rather than a filter per section on every render. */
  const attachmentsByScenario = useMemo(() => {
    const map = new Map<number, LabScenarioItem[]>();

    (attachments.data || []).forEach((attachment) =>
      map.set(attachment.scenario, [...(map.get(attachment.scenario) || []), attachment]),
    );
    map.forEach((rows) =>
      rows.sort((left, right) => (left.order || 0) - (right.order || 0) || left.id - right.id),
    );

    return map;
  }, [attachments.data]);

  const deviceById = useMemo(
    () => new Map((devices.data || []).map((device) => [device.id, device])),
    [devices.data],
  );

  /**
   * The library, indexed by the sections it is attached to.
   *
   * Grouped from each device's own link rows rather than from a column, because a device has no
   * section: the same box is in as many as use it, so it appears under each of them here.
   */
  const devicesByScenario = useMemo(() => {
    const map = new Map<number, LabDevice[]>();

    (devices.data || []).forEach((device) =>
      (device.scenario_links || []).forEach((link) =>
        map.set(link.scenario, [...(map.get(link.scenario) || []), device]),
      ),
    );
    map.forEach((rows) =>
      rows.sort((left, right) => (left.order || 0) - (right.order || 0) || left.id - right.id),
    );

    return map;
  }, [devices.data]);

  /** The attachment row for one (section, device), which is what a detach deletes. */
  const deviceLinkFor = useCallback(
    (scenarioId: number, deviceId: number): LabScenarioDeviceLink | null =>
      (deviceById.get(deviceId)?.scenario_links || []).find(
        (link) => link.scenario === scenarioId,
      ) || null,
    [deviceById],
  );

  /**
   * Every device the lab brings, named with the sections it comes in through, for the addressing
   * panel.
   *
   * Deduplicated by device, and that is load-bearing rather than tidiness: one box attached to two
   * of this lab's sections would otherwise be listed twice, and the panel writes one override per
   * (publication, device) - two rows for the same pair is a uniqueness error the second save finds.
   */
  const labDevices = useMemo(() => {
    const seen = new Map<number, LabDevice & { scenario_name?: string }>();

    links.forEach((link) => {
      const sectionName =
        link.scenario_name || scenarioById.get(link.scenario)?.name || 'Section';

      (devicesByScenario.get(link.scenario) || []).forEach((device) => {
        const already = seen.get(device.id);

        if (already) {
          already.scenario_name = `${already.scenario_name}, ${sectionName}`;
        } else {
          seen.set(device.id, { ...device, scenario_name: sectionName });
        }
      });
    });

    return [...seen.values()];
  }, [links, devicesByScenario, scenarioById]);

  const busy =
    attachScenarios.isPending ||
    detachScenario.isPending ||
    reorder.isPending ||
    attachItems.isPending ||
    detachItem.isPending ||
    attachDevices.isPending ||
    detachDevice.isPending ||
    compile.isPending ||
    saveLink.isPending;

  /** The section the tree is working in, for the "+ New" targets and the slide-over defaults. */
  const contextScenarioId =
    selected.kind === 'scenario' || selected.kind === 'item' || selected.kind === 'device'
      ? selected.scenarioId
      : null;

  const toggle = (scenarioId: number) =>
    setExpanded((current) =>
      current.includes(scenarioId)
        ? current.filter((entry) => entry !== scenarioId)
        : [...current, scenarioId],
    );

  const open = useCallback(
    (scenarioId: number) =>
      setExpanded((current) =>
        current.includes(scenarioId) ? current : [...current, scenarioId],
      ),
    [],
  );

  // The selection can be set from outside - the shell's breadcrumb, a library "open in builder" -
  // and a selected row inside a collapsed section is a selection nobody can see.
  useEffect(() => {
    if (contextScenarioId) open(contextScenarioId);
  }, [contextScenarioId, open]);

  const sectionStatus = (link: LabScenarioLink): { status: LabAuthoringStatus; title: string } => {
    if (!link.enabled) {
      return {
        status: 'retired',
        title: 'Detached from this lab without being deleted: still linked, compiles to nothing.',
      };
    }

    if (link.scenario_enabled === false) {
      return {
        status: 'retired',
        title: 'The section itself is disabled, so it contributes nothing to any lab that has it.',
      };
    }

    const rows = attachmentsByScenario.get(link.scenario) || [];

    if (!rows.length) {
      return {
        status: 'failing',
        title: 'Nothing is attached to this section, so it compiles to no steps at all.',
      };
    }

    if (!(devicesByScenario.get(link.scenario) || []).length) {
      return {
        status: 'failing',
        title: 'The section has no devices, so its items have nothing to run against.',
      };
    }

    return { status: 'untested', title: `${rows.length} items attached.` };
  };

  const itemStatus = (
    attachment: LabScenarioItem,
  ): { status: LabAuthoringStatus; title: string } => {
    if (!attachment.enabled) {
      return { status: 'retired', title: 'Detached: still listed here, compiles to nothing.' };
    }

    if (
      attachment.item_device_type_key &&
      attachment.device_type_key &&
      attachment.item_device_type_key !== attachment.device_type_key
    ) {
      return {
        status: 'failing',
        title: `Written for ${attachment.item_device_type_key}, applied to a ${attachment.device_type_key}.`,
      };
    }

    const item = itemById.get(attachment.item);
    const blockers = item ? labItemBlockers(item) : [];

    if (blockers.length) return { status: 'running', title: blockers.join(' ') };

    return { status: 'untested', title: 'Nothing has been compiled from this attachment yet.' };
  };

  const deviceStatus = (device: LabDevice): { status: LabAuthoringStatus; title: string } => {
    // The jump host is looked up so the shared-library check can run: a box reached through another
    // one, attached to a section that other one is not in, resolves to no jump host at run time.
    const blockers = labDeviceBlockers(device, {
      jumpHost: device.jump_host ? deviceById.get(device.jump_host) || null : null,
    });

    if (blockers.length) {
      return {
        status: device.address_template ? 'running' : 'failing',
        title: blockers.join(' '),
      };
    }

    return { status: 'healthy', title: 'Reachable, credentialed and typed.' };
  };

  /* ----------------------------------------------------------------------- *
   * Mutations
   * ----------------------------------------------------------------------- */

  const setLinkEnabled = async (link: LabScenarioLink, enabled: boolean) => {
    try {
      await saveLink.mutateAsync({
        id: link.id,
        lab: link.lab,
        scenario: link.scenario,
        order: link.order,
        enabled,
        notes: link.notes || '',
      });
      toasts.saved(
        `${link.scenario_name}: ${enabled ? 'back in this lab' : 'switched off in this lab only'}`,
      );
    } catch (exc) {
      toasts.failed('change that section', exc);
    }
  };

  const detachSection = async (link: LabScenarioLink) => {
    // eslint-disable-next-line no-restricted-globals
    if (
      !window.confirm(
        `Remove ${link.scenario_name} from this lab? The section itself is not deleted - it stays in the library and in every other lab that uses it.`,
      )
    ) {
      return;
    }

    try {
      await detachScenario.mutateAsync(link.id);
      toasts.detached(link.scenario_name || 'that section', row?.name || 'the lab');

      if (selected.kind !== 'lab' && 'scenarioId' in selected && selected.scenarioId === link.scenario) {
        onSelect({ kind: 'lab', label: row?.name || 'Lab' });
      }
    } catch (exc) {
      toasts.failed('detach that section', exc);
    }
  };

  /**
   * Written as a whole new order rather than one moved row.
   *
   * `order` is a position among siblings, so moving section three to the top changes what one and two
   * mean as well. Sending only the moved row leaves two sections claiming the same position, which
   * the API accepts and the runtime then resolves by id - silently, and differently from what the
   * tree shows.
   */
  const move = async (dragged: number, target: number) => {
    if (dragged === target) return;

    const from = links.findIndex((link) => link.id === dragged);
    const to = links.findIndex((link) => link.id === target);

    if (from < 0 || to < 0) return;

    const next = [...links];
    const [moved] = next.splice(from, 1);

    next.splice(to, 0, moved);

    try {
      await reorder.mutateAsync(next);
      toasts.saved('the section order');
    } catch (exc) {
      toasts.failed('reorder the sections', exc);
    }
  };

  const detachAttachment = async (attachment: LabScenarioItem) => {
    // eslint-disable-next-line no-restricted-globals
    if (
      !window.confirm(
        `Detach ${attachment.item_name} from this section? The item stays in the library. Anything that was pulled in only as its prerequisite is detached with it.`,
      )
    ) {
      return;
    }

    try {
      await detachItem.mutateAsync({
        scenarioId: attachment.scenario,
        attachmentId: attachment.id,
      });
      toasts.detached(attachment.item_name || 'that item', 'the section');

      if (selected.kind === 'item' && selected.attachmentId === attachment.id) {
        onSelect({
          kind: 'scenario',
          scenarioId: attachment.scenario,
          linkId: links.find((link) => link.scenario === attachment.scenario)?.id || 0,
          label: scenarioById.get(attachment.scenario)?.name || 'Section',
        });
      }
    } catch (exc) {
      toasts.failed('detach that item', exc);
    }
  };

  /**
   * Take a device out of one section, and out of nothing else.
   *
   * The device is not deleted, and the wording says so before the click rather than after: a box is
   * shared by default here - every pod is the same template - so a delete would take it out from
   * under labs whose authors never opened this tree. The items attached to it in *this* section are
   * what the API refuses over, which is why that is named too.
   */
  const detachBox = async (scenarioId: number, device: LabDevice) => {
    const link = deviceLinkFor(scenarioId, device.id);

    if (!link) return;

    const sectionName = scenarioById.get(scenarioId)?.name || 'this section';
    const elsewhere = (device.scenario_links || []).length - 1;

    // eslint-disable-next-line no-restricted-globals
    if (
      !window.confirm(
        `Remove ${device.display_name || device.device_key} from ${sectionName}? The device stays in the library${
          elsewhere > 0
            ? ` and in the ${elsewhere} other section${elsewhere === 1 ? '' : 's'} that use it`
            : ''
        }. Anything attached to it in this section has to be detached first.`,
      )
    ) {
      return;
    }

    try {
      await detachDevice.mutateAsync(link.id);
      toasts.detached(device.display_name || device.device_key, sectionName);

      if (selected.kind === 'device' && selected.deviceId === device.id) {
        onSelect({
          kind: 'scenario',
          scenarioId,
          linkId: links.find((entry) => entry.scenario === scenarioId)?.id || 0,
          label: sectionName,
        });
      }
    } catch (exc) {
      toasts.failed('detach that device', exc);
    }
  };

  const recompile = async (scenarioId: number) => {
    try {
      const result = await compile.mutateAsync(scenarioId);

      toasts.said(
        `Compiled: ${result.steps_created ?? 0} steps and ${
          result.criteria_created ?? 0
        } criteria written. Steps a run has already been scored against are switched off rather than rewritten.`,
      );
    } catch (exc) {
      toasts.failed('compile that section', exc);
    }
  };

  /* ----------------------------------------------------------------------- *
   * The tree
   * ----------------------------------------------------------------------- */

  const labRowActions: LabRowAction[] = [
    {
      key: 'publications',
      label: 'Where it is delivered',
      asset: arrowSquareOut,
      onSelect: () => onSelect({ kind: 'lab', label: row?.name || 'Lab' }),
    },
  ];

  const sectionActions = (link: LabScenarioLink): LabRowAction[] => {
    const actions: LabRowAction[] = [
      {
        key: 'select',
        label: 'Open this section',
        asset: pencilSimple,
        onSelect: () =>
          onSelect({
            kind: 'scenario',
            scenarioId: link.scenario,
            linkId: link.id,
            label: link.scenario_name || 'Section',
          }),
      },
    ];

    if (onOpenInLibrary) {
      actions.push({
        key: 'library',
        label: 'View in library',
        asset: arrowSquareOut,
        onSelect: () => onOpenInLibrary('scenario', link.scenario),
      });
    }

    if (!canEdit) return actions;

    return [
      ...actions,
      {
        key: 'compile',
        label: 'Compile this section',
        asset: arrowsClockwise,
        onSelect: () => recompile(link.scenario),
      },
      {
        key: 'enabled',
        label: link.enabled ? 'Switch off in this lab' : 'Switch back on in this lab',
        asset: copy,
        onSelect: () => setLinkEnabled(link, !link.enabled),
      },
      {
        key: 'detach',
        label: 'Remove from this lab',
        asset: x,
        destructive: true,
        onSelect: () => detachSection(link),
      },
    ];
  };

  const attachmentActions = (attachment: LabScenarioItem): LabRowAction[] => {
    const actions: LabRowAction[] = [
      {
        key: 'select',
        label: 'Open this attachment',
        asset: pencilSimple,
        onSelect: () =>
          onSelect({
            kind: 'item',
            scenarioId: attachment.scenario,
            attachmentId: attachment.id,
            itemId: attachment.item,
            label: attachment.item_name || 'Item',
          }),
      },
    ];

    if (onOpenInLibrary) {
      actions.push({
        key: 'library',
        label: 'View in library',
        asset: arrowSquareOut,
        onSelect: () => onOpenInLibrary('item', attachment.item),
      });
    }

    if (!canEdit) return actions;

    return [
      ...actions,
      {
        key: 'detach',
        label: 'Detach from this section',
        asset: x,
        destructive: true,
        onSelect: () => detachAttachment(attachment),
        disabled: attachment.added_as_prerequisite,
        disabledReason: attachment.added_as_prerequisite
          ? `Pulled in because ${
              attachment.required_by_key || 'another item'
            } needs it. Detach that item instead, and this goes with it.`
          : undefined,
      },
    ];
  };

  const deviceActions = (scenarioId: number, device: LabDevice): LabRowAction[] => {
    const actions: LabRowAction[] = [
      {
        key: 'select',
        label: 'Open this device',
        asset: pencilSimple,
        onSelect: () =>
          onSelect({
            kind: 'device',
            scenarioId,
            deviceId: device.id,
            label: device.display_name || device.device_key,
          }),
      },
    ];

    if (onOpenInLibrary) {
      actions.push({
        key: 'library',
        label: 'View in library',
        asset: arrowSquareOut,
        onSelect: () => onOpenInLibrary('device', device.id),
      });
    }

    if (!canEdit) return actions;

    return [
      ...actions,
      {
        key: 'detach',
        label: 'Remove from this section',
        asset: x,
        destructive: true,
        onSelect: () => detachBox(scenarioId, device),
      },
    ];
  };

  const renderSection = (link: LabScenarioLink) => {
    const scenario = scenarioById.get(link.scenario) || null;
    const rows = attachmentsByScenario.get(link.scenario) || [];
    const boxes = devicesByScenario.get(link.scenario) || [];
    const isOpen = expanded.includes(link.scenario);
    const status = sectionStatus(link);
    const isSelected = selected.kind !== 'lab' && 'scenarioId' in selected
      ? selected.scenarioId === link.scenario && selected.kind === 'scenario'
      : false;

    return (
      <li key={link.id}>
        <div
          className={treeRow(isSelected, flashed === `scenario-${link.scenario}`, 1)}
          draggable={canEdit}
          onDragStart={() => setDragLinkId(link.id)}
          onDragEnd={() => setDragLinkId(null)}
          onDragOver={(evt) => {
            if (dragLinkId && dragLinkId !== link.id) evt.preventDefault();
          }}
          onDrop={(evt) => {
            evt.preventDefault();
            if (dragLinkId) move(dragLinkId, link.id);
            setDragLinkId(null);
          }}
        >
          {canEdit && <LabDragHandle label={`Reorder ${link.scenario_name}`} />}

          <LabTwisty
            open={isOpen}
            empty={!rows.length && !boxes.length}
            onToggle={() => toggle(link.scenario)}
            label={`Expand ${link.scenario_name}`}
          />

          <LabStatusDot status={status.status} title={status.title} />

          <button
            type="button"
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-left text-sm"
            onClick={() =>
              onSelect({
                kind: 'scenario',
                scenarioId: link.scenario,
                linkId: link.id,
                label: link.scenario_name || 'Section',
              })
            }
          >
            <span className="truncate">{link.scenario_name || `Scenario ${link.scenario}`}</span>
            <span className="ml-2 text-xs text-gray-500">
              {rows.length || 'no'} item{rows.length === 1 ? '' : 's'} • {boxes.length || 'no'}{' '}
              device{boxes.length === 1 ? '' : 's'}
              {scenario && (scenario.lab_links || []).length > 1
                ? ` • in ${(scenario.lab_links || []).length} labs`
                : ''}
            </span>
          </button>

          <LabRowMenu actions={sectionActions(link)} label={`${link.scenario_name} actions`} />
        </div>

        {isOpen && (
          <ul className="m-0 list-none border-l border-gray-200 pl-4">
            {rows.map((attachment) => {
              const item = itemById.get(attachment.item) || null;
              const status2 = itemStatus(attachment);
              const needs = (item?.prerequisite_keys as string[] | undefined) || [];

              return (
                <li key={attachment.id}>
                  <div
                    className={treeRow(
                      selected.kind === 'item' && selected.attachmentId === attachment.id,
                      flashed === `item-${attachment.id}`,
                      2,
                    )}
                  >
                    <span className="w-5" />
                    <LabStatusDot status={status2.status} title={status2.title} />

                    <button
                      type="button"
                      className="min-w-0 flex-1 border-0 bg-transparent p-0 text-left text-sm"
                      onClick={() =>
                        onSelect({
                          kind: 'item',
                          scenarioId: attachment.scenario,
                          attachmentId: attachment.id,
                          itemId: attachment.item,
                          label: attachment.item_name || 'Item',
                        })
                      }
                    >
                      <span className="truncate">{attachment.item_name}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        on {attachment.device_name || attachment.device_key || 'no device'}
                      </span>
                    </button>

                    {/* The chain-link the layout spec asks for, as a count with the names in its
                        title: a prerequisite is why a row cannot simply be moved. */}
                    <LabDependencyMark names={needs} />

                    <LabRowMenu
                      actions={attachmentActions(attachment)}
                      label={`${attachment.item_name} actions`}
                    />
                  </div>
                </li>
              );
            })}

            {boxes.map((device) => {
              const status3 = deviceStatus(device);

              return (
                <li key={`device-${device.id}`}>
                  <div
                    className={treeRow(
                      selected.kind === 'device' && selected.deviceId === device.id,
                      flashed === `device-${device.id}`,
                      2,
                    )}
                  >
                    <span className="w-5" />
                    <LabStatusDot status={status3.status} title={status3.title} />

                    <button
                      type="button"
                      className="min-w-0 flex-1 border-0 bg-transparent p-0 text-left text-xs text-gray-600"
                      onClick={() =>
                        onSelect({
                          kind: 'device',
                          scenarioId: link.scenario,
                          deviceId: device.id,
                          label: device.display_name || device.device_key,
                        })
                      }
                    >
                      <span className="truncate">
                        {device.display_name || device.device_key}
                      </span>
                      <span className="ml-2 font-mono">
                        {device.address_template || 'no address'}
                      </span>
                    </button>

                    {/* How many sections have this box, which is what the tree cannot show by
                        drawing it once: this row is the same device in each of them. */}
                    <LabUsageBadge
                      usages={(device.scenario_links || []).map((entry) => ({
                        key: `section-${entry.id}`,
                        label:
                          entry.scenario_name ||
                          entry.scenario_slug ||
                          `scenario ${entry.scenario}`,
                        kind: 'section',
                        inactive: entry.enabled === false || entry.scenario_enabled === false,
                      }))}
                      subject={device.display_name || device.device_key}
                      emptyLabel="Here only"
                    />

                    <LabRowMenu
                      actions={deviceActions(link.scenario, device)}
                      label={`${device.display_name || device.device_key} actions`}
                    />
                  </div>
                </li>
              );
            })}

            {canEdit && (
              <li className="flex flex-wrap gap-2 py-1 pl-5">
                <StbButton
                  role="add"
                  size="xsmall"
                  onClick={async () => setCreating({ kind: 'item', scenarioId: link.scenario })}
                >
                  + Add new item
                </StbButton>
                <StbButton
                  role="default"
                  size="xsmall"
                  onClick={async () => setAttaching({ kind: 'item', scenarioId: link.scenario })}
                >
                  + Attach existing item
                </StbButton>
                {/* Two buttons here as well, now that devices are a library. Every pod is built
                    from the same template, so attaching the box that already exists is the common
                    case and authoring a new one is the exception - which is the opposite of how it
                    read when a device belonged to one section. */}
                <StbButton
                  role="add"
                  size="xsmall"
                  onClick={async () => setCreating({ kind: 'device', scenarioId: link.scenario })}
                >
                  + Add new device
                </StbButton>
                <StbButton
                  role="default"
                  size="xsmall"
                  onClick={async () => setAttaching({ kind: 'device', scenarioId: link.scenario })}
                >
                  + Attach existing device
                </StbButton>
              </li>
            )}
          </ul>
        )}
      </li>
    );
  };

  /* ----------------------------------------------------------------------- *
   * The detail panel
   * ----------------------------------------------------------------------- */

  const detail = () => {
    if (!row) return null;

    if (selected.kind === 'lab') return <LabDetail lab={row} publications={publications} />;

    if (selected.kind === 'publication') {
      const publication = publications.find(
        (candidate) => candidate.id === selected.configurationId,
      );

      if (!publication) {
        return <LabMessage tone="warning">That publication is no longer on this lab.</LabMessage>;
      }

      return (
        <div className="flex flex-col gap-4">
          <PublicationHeader publication={publication} />
          <LabDeviceAddressPanel
            configuration={publication}
            devices={labDevices}
            overrides={publication.device_addresses || []}
            loading={devices.isLoading}
            error={devices.error}
            canEdit={canEdit}
          />
        </div>
      );
    }

    const scenarioId = selected.scenarioId;
    const scenario = scenarioById.get(scenarioId) || null;
    const rows = attachmentsByScenario.get(scenarioId) || [];

    if (selected.kind === 'device') {
      const device = (devicesByScenario.get(scenarioId) || []).find(
        (candidate) => candidate.id === selected.deviceId,
      );

      if (!device) {
        return <LabMessage tone="warning">That device is no longer in this section.</LabMessage>;
      }

      const applied = rows.filter((attachment) => attachment.device === device.id);

      return (
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="m-0 text-base font-semibold">
              {device.display_name || device.device_key}
            </h3>
            <p className="m-0 text-xs text-gray-500">
              Attached to {scenario?.name || 'this section'}, reached at{' '}
              <span className="font-mono">
                {device.address_template || 'no address'}
                {device.port ? `:${device.port}` : ''}
              </span>{' '}
              over {device.transport}
              {device.device_type_name
                ? ` as ${device.device_type_name}${
                    device.product_name ? ` (${device.product_name})` : ''
                  }`
                : ''}
              . Every publication of every lab using this section reaches it here unless it
              overrides the address.
            </p>
          </div>

          {/* The library warning, in the same words the item panel uses: the row below is a
              catalogue entry shown as attached here, not one that belongs here. */}
          {(device.scenario_links || []).length > 1 && (
            <LabMessage tone="warning">
              {`This device is attached to ${
                (device.scenario_links || []).length
              } sections. Editing it in the library changes it for all of them, and for every lab those sections are in - only the address is per publication.`}
            </LabMessage>
          )}

          <LabBlockerList
            blockers={labDeviceBlockers(device, {
              jumpHost: device.jump_host ? deviceById.get(device.jump_host) || null : null,
            })}
            heading="On this device:"
          />

          <LabSection
            title="What is applied here"
            hint="The items attached to this box in this section."
          >
            {applied.length ? (
              <div className="flex flex-wrap gap-1">
                {applied.map((attachment) => (
                  <LabChip
                    key={attachment.id}
                    label={attachment.item_name || `item ${attachment.item}`}
                    hint={
                      attachment.item_device_type_key &&
                      attachment.item_device_type_key !== device.device_type_key
                        ? `Written for ${attachment.item_device_type_key}`
                        : undefined
                    }
                    implied={attachment.added_as_prerequisite}
                  />
                ))}
              </div>
            ) : (
              <p className="m-0 text-xs text-gray-500">
                Nothing is applied to this device yet, so it is provisioned and left alone.
              </p>
            )}
          </LabSection>
        </div>
      );
    }

    if (selected.kind === 'item') {
      const attachment = rows.find((candidate) => candidate.id === selected.attachmentId);
      const item = itemById.get(selected.itemId) || null;

      if (!attachment || !item) {
        return (
          <LabMessage tone="warning">That attachment is no longer in this section.</LabMessage>
        );
      }

      return (
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="m-0 text-base font-semibold">{item.name}</h3>
            <p className="m-0 text-xs text-gray-500">
              {labItemSummary(item)} • on{' '}
              {attachment.device_name || attachment.device_key || 'no device'}
            </p>
          </div>

          {/* Said before anything is edited: this is a catalogue row, and the panel is showing it
              as attached here rather than as belonging here. */}
          {(item.attachment_count ?? 0) > 1 && (
            <LabMessage tone="warning">
              {`This item is attached in ${item.attachment_count} places. Editing it in the library changes all of them; the option values below belong to this section alone.`}
            </LabMessage>
          )}

          <LabBlockerList blockers={labItemBlockers(item)} heading="On this item:" />

          {/* The existing panel already owns option overrides, ordering and the compile report,
              which are per-attachment edits. Reimplementing them here would be a second place for
              the merge rules to be got wrong. */}
          <LabScenarioItemsPanel scenarioId={scenarioId} canEdit={canEdit} />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <div>
          <h3 className="m-0 text-base font-semibold">{scenario?.name || 'Section'}</h3>
          <p className="m-0 text-xs text-gray-500">
            {scenario?.description || 'No description.'}
          </p>
        </div>

        {!!scenario && (scenario.lab_links || []).length > 1 && (
          <LabMessage tone="warning">
            {`This section is in ${
              (scenario.lab_links || []).length
            } labs. What is attached to it, and in what order, is shared by all of them - only the addressing is per publication.`}
          </LabMessage>
        )}

        <LabDependencyView
          scenarioName={scenario?.name || 'this section'}
          attachments={rows}
          catalogue={catalogue.data || []}
          statusOf={(attachment) => itemStatus(attachment)}
          onOpenItem={(itemId) => {
            const found = rows.find((candidate) => candidate.item === itemId);

            if (found) {
              onSelect({
                kind: 'item',
                scenarioId,
                attachmentId: found.id,
                itemId,
                label: found.item_name || 'Item',
              });
            }
          }}
        />

        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <StbButton role="default" size="small" onClick={async () => recompile(scenarioId)}>
              Compile this section
            </StbButton>
          </div>
        )}

        {/* What the compile produced, and the checks it is judged by. Collapsed, because the
            attached items above are what an author edits and these are what to read when a run
            did the wrong thing. */}
        <SectionWork scenarioId={scenarioId} canEdit={canEdit} />
      </div>
    );
  };

  /* ----------------------------------------------------------------------- *
   * Creating, in a panel beside the tree
   * ----------------------------------------------------------------------- */

  const slideOver = () => {
    if (!creating) return null;

    if (creating.kind === 'scenario') {
      return (
        <CreateScenario
          labId={labId}
          labName={row?.name || 'this lab'}
          startOrder={(links[links.length - 1]?.order ?? -10) + 10}
          onClose={() => setCreating(null)}
          onCreated={(scenario, attached) => {
            setCreating(null);
            flash(`scenario-${scenario.id}`);

            if (attached) {
              open(scenario.id);
              onSelect({
                kind: 'scenario',
                scenarioId: scenario.id,
                linkId: 0,
                label: scenario.name,
              });
            }
          }}
        />
      );
    }

    const scenario = scenarioById.get(creating.scenarioId) || null;

    if (!scenario) return null;

    if (creating.kind === 'device') {
      return (
        <CreateDevice
          scenario={{ ...scenario, devices: devicesByScenario.get(scenario.id) || [] }}
          deviceTypes={deviceTypes.data || []}
          libraryDevices={devices.data || []}
          onClose={() => setCreating(null)}
          onCreated={(device, attached) => {
            setCreating(null);
            flash(`device-${device.id}`);

            // Only selected when it is actually in the tree. A device saved to the library alone is
            // not under this section, and selecting it would show a panel for a row the tree beside
            // it does not contain.
            if (attached) {
              open(scenario.id);
              onSelect({
                kind: 'device',
                scenarioId: scenario.id,
                deviceId: device.id,
                label: device.display_name || device.device_key,
              });
            }
          }}
        />
      );
    }

    return (
      <CreateItem
        scenario={scenario}
        devices={devicesByScenario.get(scenario.id) || []}
        attachedItemIds={(attachmentsByScenario.get(scenario.id) || []).map(
          (attachment) => attachment.item,
        )}
        catalogue={catalogue.data || []}
        deviceTypes={deviceTypes.data || []}
        methods={methods.data || []}
        onNewDevice={() => setCreating({ kind: 'device', scenarioId: scenario.id })}
        onClose={() => setCreating(null)}
        onCreated={(item, attached) => {
          setCreating(null);
          flash(`item-${item.id}`);

          if (attached) open(scenario.id);
        }}
      />
    );
  };

  /* ----------------------------------------------------------------------- *
   * Attaching, from the library
   * ----------------------------------------------------------------------- */

  const attachedScenarioIds = useMemo(() => links.map((link) => link.scenario), [links]);

  const picker = () => {
    if (!attaching) return null;

    if (attaching.kind === 'scenario') {
      return (
        <LabAttachPicker<LabScenario>
          open
          title={`Attach sections to ${row?.name || 'this lab'}`}
          subtitle="Sections are shared. Attaching one here does not copy it, so a later fix to it reaches every lab that uses it - which is the point."
          candidates={scenarios.data || []}
          loading={scenarios.isLoading}
          error={scenarios.error}
          nameOf={(scenario) => (
            <span>
              <span className="font-medium">{scenario.name}</span>
              <span className="ml-2 font-mono text-xs text-gray-500">{scenario.slug}</span>
            </span>
          )}
          search={(scenario, term) =>
            `${scenario.name} ${scenario.slug} ${scenario.description}`
              .toLowerCase()
              .includes(term)
          }
          summaryOf={(scenario) =>
            [
              scenario.enabled ? null : 'disabled',
              scenario.bootstrap_only ? 'bootstrap only' : null,
              scenario.course_name || 'no course linked',
            ]
              .filter(Boolean)
              .join(' • ')
          }
          usageOf={(scenario) =>
            (scenario.lab_links || []).length
              ? `Already a section of ${(scenario.lab_links || []).length} lab${
                  (scenario.lab_links || []).length === 1 ? '' : 's'
                }`
              : 'Not in any lab yet'
          }
          attachedIds={attachedScenarioIds}
          attachedReason={() => 'Already a section of this lab.'}
          submitLabel="Attach"
          emptyMessage="There are no sections in the library yet. Author one with + Add new section."
          onClose={() => setAttaching(null)}
          onAttach={async (picked) => {
            await attachScenarios.mutateAsync({
              labId,
              scenarioIds: picked.map((entry) => entry.row.id),
              startOrder: (links[links.length - 1]?.order ?? -10) + 10,
            });
            toasts.saved(
              picked.length === 1
                ? `${picked[0].row.name} as a section of this lab`
                : `${picked.length} sections onto this lab`,
            );
            picked.forEach((entry) => flash(`scenario-${entry.row.id}`));
          }}
        />
      );
    }

    const scenario = scenarioById.get(attaching.scenarioId) || null;
    const boxes = devicesByScenario.get(attaching.scenarioId) || [];

    if (attaching.kind === 'device') {
      return (
        <LabAttachPicker<LabDevice>
          open
          title={`Attach devices to ${scenario?.name || 'this section'}`}
          subtitle="Devices are shared. Every pod is built from the same dCloud template, so the box authored once is the box every section uses - attaching it here does not copy it, and a publication that got a different address can override that alone."
          candidates={devices.data || []}
          loading={devices.isLoading}
          error={devices.error}
          nameOf={(device) => (
            <span>
              <span className="font-medium">{device.display_name || device.device_key}</span>
              <span className="ml-2 font-mono text-xs text-gray-500">{device.device_key}</span>
            </span>
          )}
          search={(device, term) =>
            `${device.display_name} ${device.device_key} ${device.role} ${device.address_template} ${
              device.device_type_name || ''
            } ${device.product_name || ''}`
              .toLowerCase()
              .includes(term)
          }
          summaryOf={(device) =>
            [
              device.device_type_name || 'no type',
              device.product_name || null,
              device.address_template || 'no address',
              device.transport,
            ]
              .filter(Boolean)
              .join(' • ')
          }
          usageOf={(device) =>
            (device.scenario_links || []).length
              ? `Already in ${(device.scenario_links || []).length} section${
                  (device.scenario_links || []).length === 1 ? '' : 's'
                } - an edit to it reaches all of them`
              : 'Not in any section yet'
          }
          attachedIds={boxes.map((device) => device.id)}
          attachedReason={() => 'Already attached to this section.'}
          submitLabel="Attach"
          emptyMessage="The device library is empty. Author a box with + Add new device."
          onClose={() => setAttaching(null)}
          onAttach={async (picked) => {
            await attachDevices.mutateAsync({
              scenarioId: attaching.scenarioId,
              deviceIds: picked.map((entry) => entry.row.id),
            });
            toasts.saved(
              picked.length === 1
                ? `${
                    picked[0].row.display_name || picked[0].row.device_key
                  } onto ${scenario?.name || 'this section'}`
                : `${picked.length} devices onto ${scenario?.name || 'this section'}`,
            );
            picked.forEach((entry) => flash(`device-${entry.row.id}`));
            open(attaching.scenarioId);
          }}
        />
      );
    }

    const already = (attachmentsByScenario.get(attaching.scenarioId) || []).map(
      (attachment) => attachment.item,
    );

    return (
      <LabAttachPicker<LabConfigurationItem>
        open
        title={`Attach items to ${scenario?.name || 'this section'}`}
        subtitle="Each item is attached to one device. Prerequisites come with it, and so do theirs - a section that needs one thing gets the whole chain rather than a step that waits forever."
        candidates={catalogue.data || []}
        loading={catalogue.isLoading}
        error={catalogue.error}
        nameOf={(item) => (
          <span>
            <span className="font-medium">{item.name}</span>
            <span className="ml-2 font-mono text-xs text-gray-500">{item.key}</span>
          </span>
        )}
        search={(item, term) =>
          `${item.name} ${item.key} ${item.description}`.toLowerCase().includes(term)
        }
        summaryOf={(item) => labItemSummary(item)}
        usageOf={(item) =>
          (item.attachment_count ?? 0)
            ? `Attached in ${item.attachment_count} place${
                item.attachment_count === 1 ? '' : 's'
              } - an edit to it reaches all of them`
            : 'Not attached anywhere yet'
        }
        attachedIds={already}
        attachedReason={() =>
          'Already attached to this section. Attach it again from the section panel if a second device needs it too.'
        }
        choice={{
          label: 'Device',
          required: true,
          options: boxes.map((device) => ({
            value: String(device.id),
            label: device.display_name || device.device_key,
          })),
          defaultValue: boxes.length === 1 ? String(boxes[0].id) : undefined,
          helpText: boxes.length
            ? 'An attachment is the item plus a device, so each row needs one. An item written for another kind of box is refused.'
            : 'This section has no devices yet, and an attachment names one. Add a device first.',
        }}
        submitLabel="Attach"
        emptyMessage="The catalogue is empty. Author an item with + Add new item."
        onClose={() => setAttaching(null)}
        onAttach={async (picked) => {
          const result = await attachItems.mutateAsync({
            scenarioId: attaching.scenarioId,
            items: picked.map((entry) => ({
              item: entry.row.id,
              device: Number(entry.choice),
            })),
          });

          const pulled = result.added_as_prerequisites || [];

          toasts.said(
            `Attached ${result.attached.length} item${
              result.attached.length === 1 ? '' : 's'
            }${
              pulled.length ? `, and ${pulled.length} more that they depend on (${pulled.join(', ')})` : ''
            }.${result.compile_required ? ' Compile the section to turn them into steps.' : ''}`,
          );
          open(attaching.scenarioId);
        }}
      />
    );
  };

  /* ----------------------------------------------------------------------- *
   * Layout
   * ----------------------------------------------------------------------- */

  return (
    <div className="flex flex-col gap-3">
      <LabPanelState
        loading={lab.isLoading}
        error={lab.error}
        empty={!row}
        emptyMessage="That lab could not be loaded."
        loadingLabel="Loading the lab..."
      >
        <div className="flex flex-col gap-4 lg:flex-row">
          {/* Fixed rather than fluid: the tree is the control, and a tree that reflows as the panel
              beside it changes content makes every row move under the pointer. */}
          <div className="w-full flex-shrink-0 lg:w-96">
            <div className="rounded border border-gray-200 p-2">
              <ul className="m-0 list-none p-0">
                <li>
                  <div className={treeRow(selected.kind === 'lab', false, 0)}>
                    <span className="w-5" />
                    <LabStatusDot
                      status={row?.enabled ? 'untested' : 'retired'}
                      title={
                        row?.enabled
                          ? `${links.filter((link) => link.enabled).length} live sections, ${
                              publications.length
                            } publications.`
                          : 'The lab is disabled: no session is matched to it in any data centre.'
                      }
                    />
                    <button
                      type="button"
                      className="min-w-0 flex-1 border-0 bg-transparent p-0 text-left text-sm font-semibold"
                      onClick={() => onSelect({ kind: 'lab', label: row?.name || 'Lab' })}
                    >
                      {row?.name}
                    </button>
                    <LabRowMenu actions={labRowActions} label="Lab actions" />
                  </div>

                  <ul className="m-0 list-none border-l border-gray-200 pl-4">
                    {links.map(renderSection)}

                    {!links.length && (
                      <li className="px-2 py-2 text-xs text-gray-500">
                        No sections yet. A lab with none is authored and delivers nothing.
                      </li>
                    )}

                    {canEdit && (
                      <li className="flex flex-wrap gap-2 py-1 pl-1">
                        <StbButton
                          role="add"
                          size="xsmall"
                          onClick={async () => setCreating({ kind: 'scenario' })}
                        >
                          + Add new section
                        </StbButton>
                        <StbButton
                          role="default"
                          size="xsmall"
                          onClick={async () => setAttaching({ kind: 'scenario' })}
                        >
                          + Attach existing section
                        </StbButton>
                      </li>
                    )}
                  </ul>
                </li>
              </ul>
            </div>

            <LabStatusLegend className="mt-2" />

            {busy && (
              <p className="m-0 mt-2 text-xs text-gray-500">Writing that change...</p>
            )}
          </div>

          <div className="min-w-0 flex-1 rounded border border-gray-200 p-4">{detail()}</div>
        </div>
      </LabPanelState>

      {slideOver()}
      {picker()}
    </div>
  );
};

/* ------------------------------------------------------------------------- *
 * The lab's own panel
 * ------------------------------------------------------------------------- */

const PublicationHeader: React.FC<{ publication: LabConfiguration }> = ({ publication }) => (
  <div>
    <h3 className="m-0 text-base font-semibold">{publication.name}</h3>
    <p className="m-0 text-xs text-gray-500">
      {publication.data_center_name || 'no data centre'} • {labSizeLabel(publication.size)} •{' '}
      {publication.demo_uid ? `demo ${publication.demo_uid}` : 'no demo id - never matched'}
      {publication.enabled ? '' : ' • disabled'}
    </p>
    <LabBlockerList
      blockers={labConfigurationBlockers(publication)}
      heading="On this publication:"
    />
  </div>
);

/**
 * The demo tag on a lab: the uid, and the data centre it was read in.
 *
 * The data centre is named out of the publications already loaded rather than by fetching the
 * data-centre list for one label, and is left off when the lab is not published where its demo was
 * read. That is a legitimate state: the lab's demo link records what it was authored against, and a
 * lab can be authored in RTP and delivered nowhere yet.
 */
const labDemoTagLabel = (lab: Lab, publications: LabConfiguration[]): string => {
  const where = publications.find((entry) => entry.data_center === lab.demo_data_center);

  return `Demo ${lab.demo_uid}${where?.data_center_name ? ` in ${where.data_center_name}` : ''}`;
};

const LabDetail: React.FC<{ lab: Lab; publications: LabConfiguration[] }> = ({
  lab,
  publications,
}) => (
  <div className="flex flex-col gap-4">
    <div>
      <h3 className="m-0 text-base font-semibold">{lab.name}</h3>
      <p className="m-0 text-xs text-gray-500">
        <span className="font-mono">{lab.slug}</span>
        {lab.owner_email ? ` • ${lab.owner_email}` : ' • no owner recorded'}
        {lab.enabled ? '' : ' • disabled'}
        {' • updated '}
        <LabDate value={lab.last_updated} />
      </p>
    </div>

    <p className="m-0 text-sm text-gray-600">{lab.description || 'No description.'}</p>

    <div className="flex flex-wrap items-center gap-2 text-xs">
      {/* The demo the lab was authored against, named with the data centre it was read in: the
          same uid is a different demo everywhere else. Not what a session is matched on - that is
          the demo id on each publication, in the badge beside this. */}
      {lab.demo_uid ? (
        <LabStatusTag label={labDemoTagLabel(lab, publications)} sentiment="success" />
      ) : (
        <LabStatusTag label="No dCloud demo linked" sentiment="warning" />
      )}
      <LabUsageBadge
        subject={lab.name}
        usages={publications.map((publication) => ({
          key: `publication-${publication.id}`,
          kind: 'publication',
          label: publication.data_center_name || publication.name,
          hint: publication.demo_uid
            ? `demo ${publication.demo_uid} • ${labSizeLabel(publication.size)}`
            : 'no demo id - never matched',
          inactive: !publication.enabled,
        }))}
        emptyLabel="Nowhere - this lab is authored and never delivered"
      />
    </div>

    {/* The publications are where the lab meets dCloud, and the addressing hangs off whichever one
        is selected - so they are listed here rather than in the tree, which is about content. */}
    <LabSection
      title="Where it is delivered"
      hint="One publication per data centre, each naming the dCloud template and demo id it provisions from. The same lab has a different demo id in RTP and in London."
    >
      <LabConfigurationsPanel labId={lab.id} canEdit />
    </LabSection>
  </div>
);

/* ------------------------------------------------------------------------- *
 * What a compile produced
 * ------------------------------------------------------------------------- */

/**
 * The steps, triggers and success criteria of one section.
 *
 * Its own component so the detail fetch is a hook at the top of a render rather than a conditional
 * one inside `detail()` - and so it is only made when a section is actually selected. The list
 * endpoint the tree is built from does not carry steps or criteria; the detail one nests them, and
 * three panels each fetching their own would show three versions of the same section side by side.
 *
 * Every panel here is collapsed by default. Steps are mostly written by the compile and rewritten by
 * the next one, so the item is where a lasting change goes - these are for reading when a run did
 * something other than what the items say, which is not most visits.
 */
const SectionWork: React.FC<{ scenarioId: number; canEdit: boolean }> = ({
  scenarioId,
  canEdit,
}) => {
  const { data: scenario, isLoading, error } = useLabScenario(scenarioId, {
    enabled: !!scenarioId,
  });

  const compiled = (scenario?.steps || []).filter((step) => !!step.source_item).length;

  return (
    <LabPanelState
      loading={isLoading}
      error={error}
      empty={!scenario}
      emptyMessage="That section could not be loaded, so its steps and checks cannot be shown."
      loadingLabel="Loading this section's steps and checks..."
    >
      {scenario ? (
        <div className="flex flex-col gap-3">
          <LabSection
            title="Steps"
            hint={
              compiled
                ? `${compiled} of these were written by a compile and the next one rewrites them. Lasting changes belong on the configuration item.`
                : 'Nothing has been compiled from the attached items yet, so anything here was written by hand.'
            }
            collapsible
            defaultOpen={false}
          >
            <LabScenarioStepsPanel
              scenario={scenario}
              canEdit={canEdit}
              phases={['bootstrap', 'setup', 'teardown']}
              title="Steps"
              subtitle="What the pod is brought to, and what is undone afterwards. Checks are below."
              emptyMessage="No steps outside validation. Either nothing has been compiled from the attached items yet, or the pod comes up from its dCloud template and nothing else is done to it."
            />
          </LabSection>

          <LabSection
            title="Validation"
            hint="What is watched while the lab runs, and what opens a run in the first place."
            collapsible
            defaultOpen={false}
          >
            <div className="flex flex-col gap-4">
              <LabScenarioStepsPanel
                scenario={scenario}
                canEdit={canEdit}
                phases={['validate']}
                title="Validation steps"
                subtitle="These usually repeat - a check that runs once only says whether it was true at that moment."
                emptyMessage="No validation steps, so nothing observes whether the work was done."
              />
              <LabScenarioTriggersPanel scenario={scenario} canEdit={canEdit} />
            </div>
          </LabSection>

          <LabSection
            title="Success criteria"
            hint="What a learner is judged against. Separate from the validation steps: a step says what happened, a criterion says whether it counted."
            collapsible
            defaultOpen={false}
          >
            <LabScenarioCriteriaPanel scenario={scenario} canEdit={canEdit} />
          </LabSection>
        </div>
      ) : (
        <span />
      )}
    </LabPanelState>
  );
};

/* ------------------------------------------------------------------------- *
 * The three slide-overs
 * ------------------------------------------------------------------------- */

const CreateScenario: React.FC<{
  labId: number;
  labName: string;
  startOrder: number;
  onClose: () => void;
  onCreated: (scenario: LabScenario, attached: boolean) => void;
}> = ({ labId, labName, startOrder, onClose, onCreated }) => {
  const [draft, setDraft] = useState<Partial<LabScenario>>({ ...LAB_SCENARIO_EMPTY });
  const [destination, setDestination] = useState<LabSaveDestination>('attach');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toasts = useLabToasts();
  const save = useSaveLabRow<Partial<LabScenario> & { id?: number }>(LAB_TABLES.scenario);
  const attach = useAttachLabScenarios();

  const slug = labScenarioSlug(null, draft);
  const blockers = labScenarioFormBlockers(draft, slug);

  const handleSave = async () => {
    setError(null);

    try {
      const saved = await save.mutateAsync({ ...draft, slug });

      if (destination === 'attach') {
        await attach.mutateAsync({ labId, scenarioIds: [saved.id as number], startOrder });
      }

      toasts.saved(
        destination === 'attach' ? `${saved.name} as a section of ${labName}` : `${saved.name}`,
      );
      onCreated(saved as LabScenario, destination === 'attach');
    } catch (exc) {
      setError(labErrorMessage(exc, 'That section could not be created.'));
    }
  };

  return (
    <LabSlideOver
      open
      title="New section"
      subtitle="A section is reusable. Every other lab that needs this can attach the same one rather than copying it."
      attachTarget={labName}
      destination={destination}
      onDestinationChange={setDestination}
      saveLabel={destination === 'attach' ? 'Create and attach' : 'Create in the library'}
      saveDisabled={!!blockers.length || !!jsonError}
      saveBlockers={[...blockers, ...(jsonError ? [`Metadata: ${jsonError}`] : [])]}
      saving={save.isPending || attach.isPending}
      onSave={handleSave}
      onClose={onClose}
      size="large"
    >
      {error && <p className="m-0 mb-2 text-sm text-amber-800">{error}</p>}
      <LabScenarioForm
        scenario={null}
        draft={draft}
        onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        jsonError={jsonError}
        onJsonError={setJsonError}
      />
    </LabSlideOver>
  );
};

const CreateDevice: React.FC<{
  scenario: LabScenario;
  deviceTypes: ReturnType<typeof useLabDeviceTypes>['data'];
  /** The library, for the jump-host picker: any device may be the route to any other. */
  libraryDevices: LabDevice[];
  onClose: () => void;
  onCreated: (device: LabDevice, attached: boolean) => void;
}> = ({ scenario, deviceTypes, libraryDevices, onClose, onCreated }) => {
  const [draft, setDraft] = useState<Partial<LabDevice>>(() => labDeviceEmpty(scenario));
  const [destination, setDestination] = useState<LabSaveDestination>('attach');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toasts = useLabToasts();
  const save = useSaveLabRow<Partial<LabDevice> & { id?: number }>(LAB_TABLES.device);
  const attach = useAttachLabDevices();

  const chosenType = useMemo(
    () => (deviceTypes || []).find((candidate) => candidate.id === draft.device_type) || null,
    [deviceTypes, draft.device_type],
  );

  // Only when a family type has been chosen. Two thousand products is not a fetch to make on the
  // chance that this device turns out to need one.
  const { data: productRows, isLoading: productsLoading } = useLabProducts({
    enabled: !!chosenType?.requires_product,
  });

  const blockers = labDeviceFormBlockers(draft, { deviceType: chosenType });

  const handleSave = async () => {
    setError(null);

    let saved: LabDevice;

    try {
      saved = (await save.mutateAsync(draft)) as LabDevice;
    } catch (exc) {
      setError(labErrorMessage(exc, 'That device could not be created.'));
      return;
    }

    if (destination !== 'attach') {
      toasts.saved(`${saved.device_key} in the device library`);
      onCreated(saved, false);
      return;
    }

    try {
      await attach.mutateAsync({ scenarioId: scenario.id, deviceIds: [saved.id] });
    } catch (exc) {
      // The device exists. Reported as the half-done thing it is, rather than as a failed create -
      // saving again would refuse on the key, which is unique library-wide.
      setError(
        labErrorMessage(
          exc,
          `${saved.device_key} was created in the library, but attaching it to ${scenario.name} failed. Attach it with "+ Attach existing device".`,
        ),
      );
      return;
    }

    toasts.saved(`${saved.device_key}, attached to ${scenario.name}`);
    onCreated(saved, true);
  };

  return (
    <LabSlideOver
      open
      title="New device"
      // A destination toggle now, where there used to be a note saying there was no library to save
      // into. There is: a device is a catalogue entry and the sections it appears in are
      // attachments, so authoring the box without putting it in this section is a real answer.
      subtitle={`A box in the device library. Attaching it to ${scenario.name} puts it in every lab that uses that section, and each publication may reach it at its own address.`}
      attachTarget={scenario.name}
      destination={destination}
      onDestinationChange={setDestination}
      saveLabel={destination === 'attach' ? 'Create and attach' : 'Create in the library'}
      saveDisabled={!!blockers.length || !!jsonError}
      saveBlockers={[...blockers, ...(jsonError ? [`Attributes: ${jsonError}`] : [])]}
      saving={save.isPending || attach.isPending}
      onSave={handleSave}
      onClose={onClose}
      size="large"
    >
      {error && <p className="m-0 mb-2 text-sm text-amber-800">{error}</p>}
      <LabDeviceForm
        scenario={scenario}
        device={null}
        draft={draft}
        onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        deviceTypes={deviceTypes || []}
        products={productRows || []}
        productsLoading={productsLoading}
        libraryDevices={libraryDevices}
        jsonError={jsonError}
        onJsonError={setJsonError}
        resetKey={scenario.id}
      />
    </LabSlideOver>
  );
};

const CreateItem: React.FC<{
  scenario: LabScenario;
  devices: LabDevice[];
  attachedItemIds: number[];
  catalogue: LabConfigurationItem[];
  deviceTypes: ReturnType<typeof useLabDeviceTypes>['data'];
  methods: ReturnType<typeof useLabExecutionMethods>['data'];
  onNewDevice: () => void;
  onClose: () => void;
  onCreated: (item: LabConfigurationItem, attached: boolean) => void;
}> = ({
  scenario,
  devices,
  attachedItemIds,
  catalogue,
  deviceTypes,
  methods,
  onNewDevice,
  onClose,
  onCreated,
}) => {
  const [draft, setDraft] = useState<Partial<LabConfigurationItem>>({ ...LAB_ITEM_EMPTY });
  const [deviceId, setDeviceId] = useState<number | null>(
    devices.length === 1 ? devices[0].id : null,
  );
  const [destination, setDestination] = useState<LabSaveDestination>('attach');
  const [jsonErrors, setJsonErrors] = useState<Record<string, string | null>>({});
  const [error, setError] = useState<string | null>(null);

  const toasts = useLabToasts();
  const save = useSaveLabRow<Partial<LabConfigurationItem> & { id?: number }>(
    LAB_TABLES.configurationItem,
  );
  const attach = useAttachLabScenarioItems();

  const chosenType = useMemo(
    () => (deviceTypes || []).find((candidate) => candidate.id === draft.device_type) || null,
    [deviceTypes, draft.device_type],
  );

  // Only once a family type has been chosen, as in the device slide-over above.
  const { data: productRows, isLoading: productsLoading } = useLabProducts({
    enabled: !!chosenType?.requires_product,
  });

  const attachContext = useMemo(
    () => ({
      scenarioName: scenario.name,
      devices,
      deviceId,
      onDeviceChange: setDeviceId,
      onNewDevice,
      attachedItemIds,
    }),
    [scenario.name, devices, deviceId, onNewDevice, attachedItemIds],
  );

  // The device is only required when this is being attached. Saving to the library alone is a
  // catalogue edit, and the catalogue does not name devices.
  const blockers = labItemFormBlockers(draft, destination === 'attach' ? attachContext : null);
  const key = labSlugify(draft.name || '');
  const clash = catalogue.find((candidate) => candidate.key === key);

  const handleSave = async () => {
    setError(null);

    if (clash) {
      setError(
        `"${clash.name}" already uses the key ${key}. Attach that item instead, or name this one differently - generated steps are named from the key.`,
      );

      return;
    }

    try {
      const saved = await save.mutateAsync({ ...draft, key });

      if (destination === 'attach') {
        await attach.mutateAsync({
          scenarioId: scenario.id,
          items: [{ item: saved.id as number, device: deviceId as number }],
        });
      }

      toasts.saved(
        destination === 'attach' ? `${saved.name} onto ${scenario.name}` : `${saved.name}`,
      );
      onCreated(saved as LabConfigurationItem, destination === 'attach');
    } catch (exc) {
      setError(labErrorMessage(exc, 'That item could not be created.'));
    }
  };

  return (
    <LabSlideOver
      open
      title="New configuration item"
      subtitle="Items live in one shared catalogue. Whatever is authored here can be attached to any other section that needs the same work."
      attachTarget={scenario.name}
      destination={destination}
      onDestinationChange={setDestination}
      saveLabel={destination === 'attach' ? 'Create and attach' : 'Create in the library'}
      saveDisabled={!!blockers.length || Object.values(jsonErrors).some((entry) => !!entry)}
      saveBlockers={[
        ...blockers,
        ...Object.entries(jsonErrors)
          .filter(([, entry]) => !!entry)
          .map(([field, entry]) => `${field}: ${entry}`),
      ]}
      saving={save.isPending || attach.isPending}
      onSave={handleSave}
      onClose={onClose}
      size="xlarge"
    >
      {error && <p className="m-0 mb-2 text-sm text-amber-800">{error}</p>}
      <LabConfigurationItemForm
        item={null}
        draft={draft}
        onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        catalogue={catalogue}
        deviceTypes={deviceTypes || []}
        methods={methods || []}
        products={productRows || []}
        productsLoading={productsLoading}
        jsonErrors={jsonErrors}
        onJsonError={(field, value) =>
          setJsonErrors((current) => ({ ...current, [field]: value }))
        }
        attach={destination === 'attach' ? attachContext : null}
      />
    </LabSlideOver>
  );
};

export default LabBuilder;
